import React, { useEffect, useRef, useState } from "react";
import { message } from "antd";
import { useNavigate } from "react-router-dom";
import AdminLayout from "./components/AdminLayout";
import axios from "axios";
import "./AdminAddProduct.css";
import "./AdminUsers.css";

const PACKID_CHARS = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
const PACKID_LEN = 10;

function generatePackId(len = PACKID_LEN) {
  const bytes = new Uint32Array(len);

  if (window.crypto && window.crypto.getRandomValues) {
    window.crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < len; i++) bytes[i] = Math.floor(Math.random() * 999999);
  }

  let out = "";
  for (let i = 0; i < len; i++) out += PACKID_CHARS[bytes[i] % PACKID_CHARS.length];
  return out;
}

function generateUniquePackId(existingRows) {
  const existing = new Set(
    (existingRows || [])
      .map((r) => String(r?.packid || "").trim())
      .filter(Boolean)
  );

  let tries = 0;
  while (tries < 20) {
    const id = generatePackId();
    if (!existing.has(id)) return id;
    tries++;
  }
  return generatePackId() + "X";
}

function toNumber(value) {
  const cleaned = String(value ?? "").replace(/[^\d.]/g, "");
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : 0;
}

function calcInrPrice(usd, usdToInrRate, profitPercent) {
  const usdNum = toNumber(usd);
  const rateNum = toNumber(usdToInrRate);
  const profitNum = toNumber(profitPercent);

  if (usdNum <= 0 || rateNum <= 0) return "";

  const inrBase = usdNum * rateNum;
  const finalPrice = inrBase + (inrBase * profitNum) / 100;

  return String(Math.round(finalPrice));
}

function extractPackSortValue(item) {
  const pack = String(item?.Pack ?? "").trim();
  const name = String(item?.name ?? "").trim();
  const source = `${pack} ${name}`;
  const matches = source.match(/\d+(\.\d+)?/g);

  if (!matches || matches.length === 0) return Number.MAX_SAFE_INTEGER;
  return Math.min(...matches.map(Number));
}

function sortProductsByPackAmount(list) {
  return [...(Array.isArray(list) ? list : [])].sort((a, b) => {
    const aVal = extractPackSortValue(a);
    const bVal = extractPackSortValue(b);

    if (aVal !== bVal) return aVal - bVal;

    const aPack = String(a?.Pack ?? "");
    const bPack = String(b?.Pack ?? "");
    return aPack.localeCompare(bPack, undefined, {
      numeric: true,
      sensitivity: "base",
    });
  });
}

function buildUsdPriceMap(products) {
  const map = new Map();

  for (const item of Array.isArray(products) ? products : []) {
    const packKey = String(item?.Pack || "").trim().toLowerCase();
    const nameKey = String(item?.name || "").trim().toLowerCase();
    const usd = String(item?.price ?? "").trim();

    if (packKey) map.set(`pack:${packKey}`, usd);
    if (nameKey) map.set(`name:${nameKey}`, usd);
  }

  return map;
}

function mergeCostWithApiUsd(existingCost, products) {
  const priceMap = buildUsdPriceMap(products);

  return (Array.isArray(existingCost) ? existingCost : []).map((row) => {
    const idKey = String(row?.id || "").trim().toLowerCase();
    const amountKey = String(row?.amount || "").trim().toLowerCase();

    const matchedUsd =
      priceMap.get(`pack:${idKey}`) ||
      priceMap.get(`name:${amountKey}`) ||
      String(row?.usd_price || "").trim();

    return {
      ...row,
      usd_price: matchedUsd || "",
    };
  });
}

const AdminAddProduct = () => {
  const navigate = useNavigate();

  const mainInputRef = useRef(null);
  const bannerInputRef = useRef(null);

  const [selectedFile, setSelectedFile] = useState(null);
  const [bannerFile, setBannerFile] = useState(null);
  const [loading, setLoading] = useState(false);

  const [apiGames, setApiGames] = useState([]);
  const [selectedApiGame, setSelectedApiGame] = useState("");
  const [apiProducts, setApiProducts] = useState([]);
  const [loadingGames, setLoadingGames] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);

  const [usdToInrRate, setUsdToInrRate] = useState("");
  const [profitPercent, setProfitPercent] = useState("");
  const [resellerProfitPercent, setResellerProfitPercent] = useState("");

  const [productCategories, setProductCategories] = useState([]);
  const [galleryImages, setGalleryImages] = useState([]);

  const [form, setForm] = useState({
    name: "",
    desc: "",
    gameName: "",
    category: "",
    tag: "",
    stock: "yes",
  });

  const [cost, setCost] = useState(() => [
    {
      packid: generatePackId(),
      id: "",
      amount: "",
      price: "",
      reseller_price: "",
      p_category: "",
      pimg: "",
      usd_price: "",
      requiresUserId: false,
      requiresServerId: false,
      requiresCharName: false,
    },
  ]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const setCostField = (index, key, value) => {
    setCost((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [key]: value };
      return updated;
    });
  };

const EMPTY_COST_ROW = () => ({
  packid: generatePackId(),
  id: "",
  amount: "",
  price: "",
  reseller_price: "",
  p_category: "",
  pimg: "",
  usd_price: "",
  requiresUserId: false,
  requiresServerId: false,
  requiresCharName: false,
});

const handleAddCostField = (index) => {
  setCost((prev) => {
    const updated = [...prev];
    updated.splice(index + 1, 0, { ...EMPTY_COST_ROW(), packid: generateUniquePackId(prev) });
    return updated;
  });
};

const handleRemoveCostField = (index) => {
  setCost((prev) => {
    const updated = [...prev];
    updated.splice(index, 1);
    return updated.length ? updated : [EMPTY_COST_ROW()];
  });
};

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (
      name.startsWith("id-") ||
      name.startsWith("amount-") ||
      name.startsWith("price-") ||
      name.startsWith("reseller_price-") ||
      name.startsWith("pimg-") ||
      name.startsWith("pcat-") ||
      name.startsWith("usd_price-")
    ) {
      const idx = parseInt(name.split("-")[1], 10);

      const key = name.startsWith("amount-")
        ? "amount"
        : name.startsWith("reseller_price-")
        ? "reseller_price"
        : name.startsWith("price-")
        ? "price"
        : name.startsWith("pimg-")
        ? "pimg"
        : name.startsWith("pcat-")
        ? "p_category"
        : name.startsWith("usd_price-")
        ? "usd_price"
        : "id";

      setCostField(idx, key, value);
      return;
    }

    setForm((p) => ({ ...p, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0] || null;
    setSelectedFile(file);
  };

  const handleBannerChange = (e) => {
    const file = e.target.files?.[0] || null;
    setBannerFile(file);
  };

  const loadCostDropdowns = async () => {
    try {
      const headers = getAuthHeaders();
      const catReq = axios.get("/api/admin/get-product-category", { headers });
      const galReq = axios.get("/api/image/get-images", { headers });

      const [catRes, galRes] = await Promise.allSettled([catReq, galReq]);

      if (catRes.status === "fulfilled" && catRes.value.data?.success) {
        setProductCategories(Array.isArray(catRes.value.data.data) ? catRes.value.data.data : []);
      } else {
        setProductCategories([]);
      }

      if (galRes.status === "fulfilled" && galRes.value.data?.success) {
        setGalleryImages(Array.isArray(galRes.value.data.data) ? galRes.value.data.data : []);
      } else {
        setGalleryImages([]);
      }
    } catch (err) {
      console.log(err);
      setProductCategories([]);
      setGalleryImages([]);
    }
  };

  const loadApiGames = async () => {
    try {
      setLoadingGames(true);
      const res = await axios.get("/api/games");
      const list = Array.isArray(res.data?.data) ? res.data.data : [];
      setApiGames(list);
    } catch (err) {
      console.error(err);
      message.error(err?.response?.data?.message || "Failed to load games");
      setApiGames([]);
    } finally {
      setLoadingGames(false);
    }
  };

  const loadApiProducts = async (gameCode) => {
    try {
      if (!gameCode) {
        setApiProducts([]);
        return;
      }

      setLoadingProducts(true);
      const res = await axios.get(`/api/products/${encodeURIComponent(gameCode)}`);
      const list = Array.isArray(res.data?.data) ? res.data.data : [];
      setApiProducts(sortProductsByPackAmount(list));
    } catch (err) {
      console.error(err);
      message.error(err?.response?.data?.message || "Failed to load products");
      setApiProducts([]);
    } finally {
      setLoadingProducts(false);
    }
  };

  useEffect(() => {
    loadCostDropdowns();
    loadApiGames();
  }, []);

const handlePrefillApiPacks = () => {
  if (!selectedApiGame) {
    return message.error("Please select a game first");
  }

  if (!Array.isArray(apiProducts) || apiProducts.length === 0) {
    return message.error("No packs found for selected game");
  }

  const sortedProducts = sortProductsByPackAmount(apiProducts);
  const tempRows = [];

  const mapped = sortedProducts.map((item) => {
    const usd = String(item?.price ?? "").trim();

    const row = {
      packid: generateUniquePackId(tempRows),
      id: String(item?.Pack || "").trim(),
      amount: String(item?.name || item?.Pack || "").trim(),
      price:          calcInrPrice(usd, usdToInrRate, profitPercent),
      reseller_price: calcInrPrice(usd, usdToInrRate, resellerProfitPercent),
      p_category: "",
      pimg: "",
      usd_price: usd,
      requiresUserId: Boolean(item?.requiresUserId),
      requiresServerId: Boolean(item?.requiresServerId),
      requiresCharName: Boolean(item?.requiresCharName),
    };

    tempRows.push(row);
    return row;
  });

  setCost(mapped);

  const selectedGameObj = apiGames.find(
    (g) => String(g.gamecode) === String(selectedApiGame)
  );

  setForm((prev) => ({
    ...prev,
    gameName: selectedGameObj?.Name || "",
    name: prev.name || selectedGameObj?.Name || "",
  }));

  message.success(`Prefilled ${mapped.length} packs`);
};

  const applyRateToAllRows = () => {
    const rateNum = toNumber(usdToInrRate);
    if (rateNum <= 0) return message.error("Please enter a valid USD to INR rate");

    setCost((prev) =>
      prev.map((row) => ({
        ...row,
        price:          calcInrPrice(row?.usd_price, usdToInrRate, profitPercent),
        reseller_price: calcInrPrice(row?.usd_price, usdToInrRate, resellerProfitPercent),
      }))
    );

    message.success("Prices applied to all rows (no decimals)");
  };

  const finalGameName =
    (apiGames.find((g) => String(g.gamecode) === String(selectedApiGame))?.gamecode || "").trim();

  const handleAddProduct = async () => {
    const name = form.name.trim();
    if (!name) return message.error("Name is required");

    const token = localStorage.getItem("token");
    if (!token) return message.error("Token missing. Please login again.");

    if (!finalGameName) {
      return message.error("Please select a game");
    }

    if (!selectedApiGame) {
      return message.error("Please select a game");
    }

    if (!Array.isArray(cost) || cost.length === 0) {
      return message.error("Add at least 1 cost row");
    }

    const badRow = cost.find(
      (c) =>
        !String(c?.packid || "").trim() ||
        !String(c?.id || "").trim() ||
        !String(c?.amount || "").trim() ||
        !String(c?.price || "").trim()
    );

    if (badRow) {
      return message.error("Cost row missing packid/id/amount/price. Please fill all cost rows.");
    }

    if (!selectedFile) return message.error("Image is required");
    if (selectedFile.size > 2 * 1024 * 1024) {
      return message.error("Selected file exceeds the 2MB size limit.");
    }

    if (bannerFile && bannerFile.size > 2 * 1024 * 1024) {
      return message.error("Banner image exceeds 2MB limit.");
    }

const payloadCost = cost.map((row) => ({
  packid:         row.packid,
  id:             row.id,
  amount:         row.amount,
  price:          row.price,
  reseller_price: row.reseller_price || "",
  p_category:     row.p_category,
  pimg:           row.pimg,
  usd_price:      row.usd_price,
  requiresUserId:   Boolean(row.requiresUserId),
  requiresServerId: Boolean(row.requiresServerId),
  requiresCharName: Boolean(row.requiresCharName),
}));

    const formData = new FormData();
    formData.append("name", name);
    formData.append("desc", form.desc);
    formData.append("gameName", finalGameName);
    formData.append("category", form.category || "");
    formData.append("tag",      form.tag      || "");
    formData.append("stock", form.stock);
    formData.append("cost", JSON.stringify(payloadCost));
    formData.append("image", selectedFile);
    formData.append("gameCode", selectedApiGame);
    formData.append("source", "api");

    if (bannerFile) formData.append("banner_image", bannerFile);

    setLoading(true);
    try {
      const res = await axios.post("/api/product/add-product", formData, {
        headers: {
          Authorization: "Bearer " + token,
          "Content-Type": "multipart/form-data",
        },
      });

      if (res.data?.success) {
        message.success(res.data.message);

        if (mainInputRef.current) mainInputRef.current.value = "";
        if (bannerInputRef.current) bannerInputRef.current.value = "";

        setSelectedFile(null);
        setBannerFile(null);

        setForm({
          name: "",
          desc: "",
          gameName: "",
          category: "",
          tag: "",
          stock: "yes",
        });

        setCost([EMPTY_COST_ROW()]);

        setSelectedApiGame("");
        setApiProducts([]);
        setUsdToInrRate("");
        setProfitPercent("");
        setResellerProfitPercent("");

        navigate("/admin-products");
      } else {
        message.error(res.data?.message || "Failed");
      }
    } catch (err) {
      console.error(err);
      message.error(err?.response?.data?.message || "Error adding product");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminLayout>
      <div className="admin-users-container">
        <div className="page-title">
          <h3 className="m-0">Add Product</h3>
        </div>
        <hr />

        <div className="add-product-container">
          <div className="form-fields mb-3">
            <small className="form-label-small">Product Image</small>
            <input
              className="w-100"
              type="file"
              accept=".jpg, .jpeg, .png, .webp"
              name="image"
              required
              ref={mainInputRef}
              onChange={handleFileChange}
            />
          </div>

          <div className="form-fields mb-3">
            <small className="form-label-small">Banner Image <span style={{color:"#aaa"}}>(optional)</span></small>
            <input
              className="w-100"
              type="file"
              accept=".jpg, .jpeg, .png, .webp"
              name="banner_image"
              ref={bannerInputRef}
              onChange={handleBannerChange}
            />
          </div>

          <div className="form-fields mb-3">
            <small className="form-label-small">Product Name</small>
            <input
              className="w-100"
              name="name"
              onChange={handleChange}
              value={form.name}
              type="text"
              placeholder="Enter product name"
            />
          </div>

          <div className="form-fields mb-3">
            <small className="form-label-small">Description <span style={{color:"#aaa"}}>(optional)</span></small>
            <textarea
              style={{ border: "1px solid #000" }}
              name="desc"
              cols="30"
              rows="3"
              placeholder="Enter description"
              className="form-control"
              onChange={handleChange}
              value={form.desc}
            />
          </div>

          <div className="form-fields mb-3">
            <small className="form-label-small">Game</small>
            <select
              className="w-100"
              value={selectedApiGame}
              onChange={async (e) => {
                const code = e.target.value;
                setSelectedApiGame(code);
                await loadApiProducts(code);
              }}
            >
              <option value="">
                {loadingGames ? "Loading games..." : "-- Select Game --"}
              </option>
              {apiGames.map((item) => (
                <option key={item.gamecode} value={item.gamecode}>
                  {item.Name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-fields mb-3">
            <small className="form-label-small">Category <span style={{color:"#aaa",fontWeight:400}}>(optional)</span></small>
            <input
              className="w-100"
              type="text"
              name="category"
              value={form.category}
              onChange={handleChange}
              placeholder="Type or pick a category"
              list="category-list"
              autoComplete="off"
            />
            <datalist id="category-list">
              {productCategories.map((c) => (
                <option key={c._id} value={c.name} />
              ))}
            </datalist>
          </div>

          <div className="form-fields mb-3">
            <small className="form-label-small">Tag <span style={{color:"#aaa",fontWeight:400}}>(optional)</span></small>
            <input
              className="w-100"
              type="text"
              name="tag"
              value={form.tag}
              onChange={handleChange}
              placeholder="e.g. Hot, New, Sale"
              autoComplete="off"
            />
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
              gap: 12,
              marginBottom: 16,
            }}
          >
            <div className="form-fields">
              <small className="form-label-small">USD → INR Rate</small>
              <input
                className="w-100"
                type="number"
                min="0"
                step="1"
                value={usdToInrRate}
                onChange={(e) => setUsdToInrRate(e.target.value)}
                placeholder="e.g. 97"
              />
            </div>

            <div className="form-fields">
              <small className="form-label-small">Price Profit %</small>
              <input
                className="w-100"
                type="number"
                min="0"
                step="1"
                value={profitPercent}
                onChange={(e) => setProfitPercent(e.target.value)}
                placeholder="e.g. 10"
              />
            </div>

            <div className="form-fields">
              <small className="form-label-small">Reseller Profit %</small>
              <input
                className="w-100"
                type="number"
                min="0"
                step="1"
                value={resellerProfitPercent}
                onChange={(e) => setResellerProfitPercent(e.target.value)}
                placeholder="e.g. 5"
              />
            </div>

            <div className="form-fields" style={{ display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
              <button
                type="button"
                className="add-product-btn"
                onClick={applyRateToAllRows}
                disabled={cost.length === 0}
              >
                Apply All Prices
              </button>
            </div>

            <div className="form-fields" style={{ display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
              <button
                type="button"
                className="add-product-btn"
                onClick={handlePrefillApiPacks}
                disabled={loadingProducts || !selectedApiGame || apiProducts.length === 0}
              >
                {loadingProducts
                  ? "Loading packs..."
                  : `Prefill Packs (${apiProducts.length})`}
              </button>
            </div>
          </div>

          <div className="form-fields mb-3">
            <small className="form-label-small">Stock Status</small>
            <select
              onChange={handleChange}
              value={form.stock}
              name="stock"
              className="w-100"
            >
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </div>

          <div className="cost-section">
            <div className="cost-header">
              <h5 className="m-0">Cost Rows</h5>
              <button type="button" className="cost-refresh" onClick={loadCostDropdowns}>
                Refresh Lists
              </button>
            </div>

            {cost.map((row, index) => (
              <div className="cost-card" key={index}>
                <div className="cost-grid">
                  <div className="cost-field">
                    <label>PackID (auto)</label>
                    <input value={row?.packid || ""} type="text" readOnly />
                  </div>

                  <div className="cost-field">
                    <label>ID</label>
                    <input
                      name={`id-${index}`}
                      onChange={handleChange}
                      value={row?.id || ""}
                      type="text"
                      placeholder="Enter id"
                    />
                  </div>

                  <div className="cost-field">
                    <label>Amount</label>
                    <input
                      name={`amount-${index}`}
                      onChange={handleChange}
                      value={row?.amount || ""}
                      type="text"
                      placeholder="Enter Amount"
                    />
                  </div>

                  <div className="cost-field">
                    <label>USD Price</label>
                    <input
                      name={`usd_price-${index}`}
                      onChange={handleChange}
                      value={row?.usd_price || ""}
                      type="text"
                      placeholder="Enter USD price"
                    />
                  </div>

                  <div className="cost-field">
                    <label>Price</label>
                    <input
                      name={`price-${index}`}
                      onChange={handleChange}
                      value={row?.price || ""}
                      type="text"
                      placeholder="Enter Price"
                    />
                  </div>

                  <div className="cost-field">
                    <label>Reseller Price <span style={{color:"#aaa",fontWeight:400}}>(optional)</span></label>
                    <input
                      name={`reseller_price-${index}`}
                      onChange={handleChange}
                      value={row?.reseller_price || ""}
                      type="text"
                      placeholder="Reseller Price (leave blank = same as Price)"
                    />
                  </div>

                  <div className="cost-field">
                    <label>P-Category (optional)</label>
                    <select
                      name={`pcat-${index}`}
                      value={row?.p_category || ""}
                      onChange={handleChange}
                    >
                      <option value="">Select p-category</option>
                      {productCategories.map((c) => (
                        <option key={c._id} value={c.name}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="cost-field">
                    <label>Gallery Image (optional)</label>
                    <select
                      value={row?.pimg || ""}
                      onChange={(e) => setCostField(index, "pimg", e.target.value)}
                    >
                      <option value="">Select gallery image</option>
                      {galleryImages.map((g) => (
                        <option key={g._id} value={g.image || ""}>
                          {g.name || "No name"}{" "}
                          {g.image ? `— ${String(g.image).split("/").pop()}` : ""}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="cost-actions">
                  <button
                    type="button"
                    className="btn-mini"
                    onClick={() => handleAddCostField(index)}
                  >
                    + Add
                  </button>
                  {index > 0 && (
                    <button
                      type="button"
                      className="btn-mini danger"
                      onClick={() => handleRemoveCostField(index)}
                    >
                      - Remove
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          <button className="add-product-btn" onClick={handleAddProduct} disabled={loading}>
            {loading ? "Adding..." : "Add Product"}
          </button>

          {apiProducts.length > 0 && (
            <div style={{ overflowX: "auto", marginTop: 24 }}>
              <h4 style={{ marginBottom: 12 }}>Available Packs</h4>
              <table className="table mt-3 bg-white text-dark">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Pack</th>
                    <th>USD</th>
                    <th>Calculated INR</th>
                    <th>Stock</th>
                  </tr>
                </thead>
                <tbody>
                  {apiProducts.map((row, idx) => (
                    <tr key={`${row?.gamecode || "game"}-${row?.Pack || idx}`}>
                      <td>{row?.name || "-"}</td>
                      <td>{row?.Pack || "-"}</td>
                      <td>{row?.price ?? "-"}</td>
                      <td>{calcInrPrice(row?.price, usdToInrRate, profitPercent) || "-"}</td>
                      <td>{row?.stockStatus || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminAddProduct;